import React from "react";
import PropTypes from "prop-types";
import { VictoryContainer, Selection } from "victory-core";
import { throttle, isFunction, defaults } from "lodash";

import moment from "moment";

const clearZoomProps = {
	moveStart: null,
	moveStartDomain: null,
	zoomStart: null,
	zoomEnd: null,
	zoomDomain: null
};

const clearZoomEvent = {
	target: "parent",
	mutation: () => clearZoomProps
};

const getEventInDomain = (evt, targetProps) => {
	const mousePosition = Selection.getSVGEventCoordinates(evt);
	return Selection.getDataCoordinates(targetProps,
		targetProps.scale, mousePosition.x, mousePosition.y);
};

const onMouseDown = (evt, targetProps) => {
	const { allowRect, allowMove, domain } = targetProps;
	if (!allowRect && !allowMove) {
		return {};
	}
	evt.preventDefault();
	let is_move = evt.ctrlKey || evt.altKey || evt.shiftKey;
	if (is_move) {
		let { x, y } = Selection.getSVGEventCoordinates(evt);
		return [{
			target: "parent",
			mutation: () => ({
				moveStart: [x, y],
				moveStartDomain: {
					x: [ domain.x[0], domain.x[1] ],
					y: [ domain.y[0], domain.y[1] ]
				}
			})
		}];
	} else {
		const { x, y } = getEventInDomain(evt, targetProps);
		return [{
			target: "parent",
			mutation: () => ({
				zoomStart: [x, y]
			})
		}];
	}
};

const makeZoomDomain = (a, b) => ({
	x: a[0] < b[0] ? [a[0], b[0]] : [b[0], a[0]],
	y: a[1] < b[1] ? [a[1], b[1]] : [b[1], a[1]]
});

const makeMoveDomain = (s, e, scale, domain) => {
	let range_x = scale.x.range();
	let plot_w = Math.abs(range_x[0] - range_x[1]);
	let domain_w = Math.abs(domain.x[0].getTime() - domain.x[1].getTime());
	let dx = (s[0] - e[0]) / plot_w * domain_w;
	let range_y = scale.y.range();
	let plot_h = Math.abs(range_y[0] - range_y[1]);
	let domain_h = Math.abs(domain.y[0] - domain.y[1]);
	let dy = (e[1] - s[1]) / plot_h * domain_h;
	let new_domain = {
		x: [domain.x[0].getTime() + dx,
			domain.x[1].getTime() + dx],
		y: [domain.y[0] + dy, domain.y[1] + dy]
	};
	return new_domain;
};

const onMouseMove = throttle((evt, targetProps) => {
	const { allowRect, zoomStart, moveStart, moveStartDomain, onDomainChange } = targetProps;
	if (!zoomStart && !moveStart) {
		return {};
	}
	/* allowRect und/oder allowMove haben sich während des Mausklicks geändert */
	if (!allowRect && !moveStart) {
		return clearZoomEvent;
	}
	if (zoomStart) {
		const { x, y } = getEventInDomain(evt, targetProps);
		const domain = makeZoomDomain(zoomStart, [x, y]);
		return [{
			target: "parent",
			mutation: () => ({
				zoomEnd: [x, y],
				zoomDomain: domain
			})
		}];
	} else if (isFunction(onDomainChange)) {
		let { x, y } = Selection.getSVGEventCoordinates(evt);
		let { scale } = targetProps;
		const new_domain = makeMoveDomain(moveStart, [x, y], scale, moveStartDomain);
		onDomainChange(new_domain, defaults({}, clearZoomProps, targetProps));
		/*return [{
			target: "parent",
			mutation: () => ({
				moveStart: [x, y],
				moveStartDomain: new_domain
			})
		}];*/
		return {};
	}
}, 16, { leading: true, trailing: false });

const onMouseUp = (evt, targetProps) => {
	const { allowRect, zoomStart, zoomEnd, moveStart, onDomainChange } = targetProps;
	if (!zoomEnd || moveStart) {
		return clearZoomEvent;
	}
	if (!allowRect) {	/* allowRect hat sich während der Auswahl geändert */
		return clearZoomEvent;
	}
	if (isFunction(onDomainChange)) {
		const domain = makeZoomDomain([moment(zoomStart[0]).valueOf(), zoomStart[1]], [moment(zoomEnd[0]).valueOf(), zoomEnd[1]]);
		onDomainChange(domain, defaults({}, clearZoomProps, targetProps));
	}
	return clearZoomEvent;
};

const onWheel = throttle((evt, targetProps) => {
	const { allowWheel, zoomStart, domain, onDomainChange } = targetProps;
	if (!allowWheel || zoomStart) {
		/* Wheel während Rechteck -> ist mir zu komplex */
		return {};
	}
	evt.preventDefault();
	const { x, y } = getEventInDomain(evt, targetProps);
	let zoom = evt.deltaY < 0 ? 0.75 : 1.25;
	let new_minY = y - (y - domain.y[0]) * zoom;
	let new_maxY = y + (domain.y[1] - y) * zoom;
	const min_max_mindestabstand = 0.1;
	if (new_maxY - new_minY < min_max_mindestabstand) {
		// Neuen Zoomfaktor ausrechnen, damit es passt
		zoom *= min_max_mindestabstand / (new_maxY - new_minY);
		if (Math.abs(zoom - 1) < 0.01) {
			// Abbrechen, das führt zu nix
			return {};
		}
		new_minY = y - (y - domain.y[0]) * zoom;
		new_maxY = y + (domain.y[1] - y) * zoom;
	}
	let new_minX = x.getTime() - (x.getTime() - domain.x[0]) * zoom;
	let new_maxX = x.getTime() + (domain.x[1] - x.getTime()) * zoom;
	if (isFunction(onDomainChange)) {
		const new_domain = makeZoomDomain(
			[new_minX, new_minY],
			[new_maxX, new_maxY]);
		onDomainChange(new_domain, targetProps);
	}
	return {};
}, 16, { leading: true, trailing: false });

export const almigZoomContainerMixin = base => class VictoryAlmigZoomContainer extends base {

	static displayName = "VictoryAlmigZoomContainer";

	static propTypes = {
		...VictoryContainer.propTypes,
		disable: PropTypes.bool,
		allowRect: PropTypes.bool,
		allowMove: PropTypes.bool,
		allowWheel: PropTypes.bool,
		onDomainChange: PropTypes.func,
		zoomRectComponent: PropTypes.element,
		zoomRectStyle: PropTypes.object
	};

	static defaultProps = {
		...VictoryContainer.defaultProps,
		allowRect: true,
		allowMove: true,
		allowWheel: true,
		zoomRectComponent: <rect/>,
		zoomRectStyle: {
			stroke: "black",
			fill: "transparent"
		}
	};

	static defaultEvents = props => {
		return [{
			target: "parent",
			eventHandlers: {
				onMouseDown: (evt, targetProps) => {
					return props.disable ? {} : onMouseDown(evt, targetProps);
				},
				onMouseMove: (evt, targetProps) => {
					return props.disable ? {} : onMouseMove(evt, targetProps);
				},
				onMouseUp: (evt, targetProps) => {
					return props.disable ? {} : onMouseUp(evt, targetProps);
				},
				onMouseLeave: () => {
					return clearZoomEvent;
				},
				onWheel: (evt, targetProps) => {
					return props.disable ? {} : onWheel(evt, targetProps);
				}
			}
		}, {
			target: "data",
			eventHandlers: props.disable ? {} : {
				onMouseOver: () => null,
				onMouseOut: () => null,
				onMouseMove: () => null
			}
		}];
	};

	getZoomRect(props) {
		const { zoomDomain } = props;
		if (!zoomDomain) {
			return null;
		}
		const { zoomRectStyle, zoomRectComponent } = props;
		const zoomRectComponentStyle = zoomRectComponent.props && zoomRectComponent.props.style;
		const { x, y } = Selection.getDomainCoordinates(props, zoomDomain);
		return React.cloneElement(zoomRectComponent, {
			width: Math.abs(x[1] - x[0]) || 1,
			height: Math.abs(y[1] - y[0]) || 1,
			x: Math.min(x[0], x[1]),
			y: Math.min(y[0], y[1]),
			cursor: "move",
			style: defaults({}, zoomRectComponentStyle, zoomRectStyle)
		});
	}

	// Overrides method in VictoryContainer
	getChildren(props) {
		const children = React.Children.toArray(props.children);
		return [...children, this.getZoomRect(props)].map((component, i) => {
			return component ? React.cloneElement(component, { key: i }) : null;
		});
	}
};

export default almigZoomContainerMixin(VictoryContainer);
